// Ported from deexoptions-mobile-app/hooks/queries/useTransactions.ts.
// Spec: deexoptions-mobile-app/docs/activity-system.md
//
// PWA divergence: mobile imports a plain `baseAxios` module. Here `useAuthAxios`
// is a hook (it needs useAuth() for the 401 auto-logout), so each hook calls it
// in its own body and the fetchers take the instance as an argument. The
// instance is a new object every render — that is safe inside `queryFn`, which
// React Query re-reads per fetch and never keys on, but it must NEVER go into a
// dependency array that gates a fetch.

import { useCallback } from "react";
import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type InfiniteData,
} from "@tanstack/react-query";
import type { AxiosInstance } from "axios";
import useAuthAxios from "../../utils/baseAxios";
import { API } from "../../constants/api";
import {
  buildSummaryQuery,
  buildTransactionsQuery,
  type ActivityFilter,
} from "../../utils/activity/filters";
import {
  normalizeTransaction,
  type ActivityItem,
} from "../../utils/activity/normalizeTransaction";
import { transactionKeys } from "./transactionKeys";
import { ACTIVITY_PAGE_LIMIT, ACTIVITY_STALE_TIME } from "./activityKeys";

// Cursor pagination: the list endpoint returns opaque cursors rather than
// page numbers / totals. `nextCursor` is passed back as `?cursor=` to fetch the
// following page; there is no total count.
interface TransactionsPageInfo {
  limit: number;
  hasNext: boolean;
  hasPrev: boolean;
  nextCursor: string | null;
  prevCursor: string | null;
}

interface TransactionsResponse {
  status: string;
  message: string;
  data: {
    transactions: any[];
    pageInfo: TransactionsPageInfo;
  };
}

const fetchTransactionsPage = async (
  axiosInstance: AxiosInstance,
  filter: ActivityFilter,
  cursor: string | undefined,
  limit: number
): Promise<TransactionsResponse> => {
  const { data } = await axiosInstance.get(
    API.getTransactions(buildTransactionsQuery(filter, cursor, limit))
  );
  return data;
};

type TransactionsSelectResult = {
  items: ActivityItem[];
};

type UseTransactionsOptions = {
  limit?: number;
  enabled?: boolean;
  refetchInterval?: number | false;
};

const useTransactions = (
  filter: ActivityFilter,
  options?: UseTransactionsOptions
) => {
  const axiosInstance = useAuthAxios();
  const limit = options?.limit ?? ACTIVITY_PAGE_LIMIT;
  return useInfiniteQuery({
    queryKey: transactionKeys.list(filter, limit),
    queryFn: async ({ pageParam }) =>
      fetchTransactionsPage(
        axiosInstance,
        filter,
        pageParam as string | undefined,
        limit
      ),
    // First page carries no cursor.
    initialPageParam: undefined as string | undefined,
    enabled: options?.enabled ?? true,
    staleTime: ACTIVITY_STALE_TIME,
    // Poll only while the user is still on the first page. Refetching an
    // infinite query re-runs *every* loaded page, so an unconditional interval
    // meant N requests per tick — six pages deep on the Activity tab was six
    // requests every 10s, forever. New and status-changed transactions land on
    // page 1 anyway; someone paged deeper is reading history, and still gets
    // fresh data on refresh or on returning to the page.
    refetchInterval: (query) => {
      const interval = options?.refetchInterval;
      if (!interval) return false;
      return (query.state.data?.pages.length ?? 0) > 1 ? false : interval;
    },
    // Refetching an infinite query re-runs *every* stored page in sequence, so
    // once the user has paged in, a routine mount/focus refetch costs N requests
    // instead of one. Conditional rather than a flat `false` so single-page
    // consumers keep their normal freshness — only the paginated case opts out,
    // and it stays fresh via the page-1 poll and the refresh button. This
    // matters more on web than on mobile: a browser tab left open all day would
    // otherwise refetch every loaded page on every focus.
    refetchOnMount: (query) => (query.state.data?.pages.length ?? 0) <= 1,
    refetchOnWindowFocus: (query) =>
      (query.state.data?.pages.length ?? 0) <= 1,
    placeholderData: keepPreviousData,
    getNextPageParam: (lastPage: TransactionsResponse) => {
      const pageInfo = lastPage?.data?.pageInfo;
      return pageInfo?.hasNext ? pageInfo.nextCursor ?? undefined : undefined;
    },
    // Normalize once per fetch — rows render the pre-computed ActivityItem.
    select: (
      data: InfiniteData<TransactionsResponse>
    ): TransactionsSelectResult => ({
      items: data.pages.flatMap((page) =>
        (page?.data?.transactions ?? []).map((tx) =>
          normalizeTransaction(tx, filter)
        )
      ),
    }),
  });
};

/**
 * Refresh for one transactions list (plus its summary).
 *
 * Refetching an infinite query re-runs every loaded page in sequence, so a
 * plain invalidate turned a refresh into N chained requests — ~4s at six pages,
 * and worse the further you'd scrolled. Truncating the cache to the first page
 * first keeps a refresh at one request whatever the scroll depth.
 *
 * Truncating in place rather than resetQueries matters: reset would blank the
 * cache and flash the skeleton loader on every refresh. This leaves the current
 * rows on screen to be replaced when the refetch lands.
 *
 * Scoped to the filter on screen — invalidating transactionKeys.all would mark
 * every cached filter combination stale, so switching filters afterwards would
 * refetch instead of hitting cache.
 */
const useRefreshTransactions = (
  filter: ActivityFilter,
  limit: number = ACTIVITY_PAGE_LIMIT
) => {
  const queryClient = useQueryClient();

  return useCallback(async () => {
    const listKey = transactionKeys.list(filter, limit);

    queryClient.setQueryData<InfiniteData<TransactionsResponse>>(
      listKey,
      (old) =>
        old && old.pages.length > 1
          ? {
              pages: old.pages.slice(0, 1),
              pageParams: old.pageParams.slice(0, 1),
            }
          : old
    );

    await Promise.all([
      queryClient.refetchQueries({ queryKey: listKey, exact: true }),
      queryClient.invalidateQueries({
        queryKey: transactionKeys.summary(filter),
      }),
    ]);
  }, [queryClient, filter, limit]);
};

// The total-USD field name differs per type (see mobile docs/summary.md):
// giftcard → totalAmountUSD, payout → totalPayoutUSD,
// crypto/billpayment → totalAmountUSDT.
export const extractSummaryTotalUSD = (summary: any): number | undefined => {
  const d = summary?.data ?? summary;
  const candidates = [d?.totalAmountUSD, d?.totalPayoutUSD, d?.totalAmountUSDT];
  const found = candidates.find((v) => typeof v === "number");
  return found as number | undefined;
};

const fetchTransactionsSummary = async (
  axiosInstance: AxiosInstance,
  filter: ActivityFilter
) => {
  const { data } = await axiosInstance.get(
    API.getTransactionsSummary(buildSummaryQuery(filter))
  );
  return data;
};

const useTransactionsSummary = (
  filter: ActivityFilter,
  options?: { enabled?: boolean }
) => {
  const axiosInstance = useAuthAxios();
  return useQuery({
    queryKey: transactionKeys.summary(filter),
    queryFn: () => fetchTransactionsSummary(axiosInstance, filter),
    enabled: options?.enabled ?? true,
    staleTime: ACTIVITY_STALE_TIME,
    placeholderData: keepPreviousData,
  });
};

export {
  useTransactions,
  useTransactionsSummary,
  useRefreshTransactions,
  fetchTransactionsPage,
};
export type { TransactionsSelectResult, UseTransactionsOptions };
